'use client';
import * as React from 'react';

import { useScroll } from '../hooks/ScrollStoreContext.hooks';
import { cn } from '../utils';

export type InputProps = React.ComponentProps<'input'> & {
  ignoreScrolling?: boolean;
  /** Merged into the WebKit picker shell (date/time types), e.g. to force a background. */
  shellClassName?: string;
};

/**
 * Same DOM + classes as ProposalReviewAndSendStep native date fields: border/chrome on a flex
 * wrapper, minimal `<input>` inside. `display:flex` on the actual picker input breaks iOS Safari.
 */
const isWebKitPickerInputType = (t: InputProps['type']): boolean =>
  t === 'date' || t === 'datetime-local' || t === 'month' || t === 'time' || t === 'week';

/**
 * The scroll guard disables the input so a mouse wheel can't change its value mid-scroll, which
 * only these types are vulnerable to. Applying it to a text field blurs it the moment any scroll
 * fires — including the scroll caused by typing into it — so those opt out.
 */
const isScrollMutableInputType = (t: InputProps['type']): boolean =>
  t === 'number' || t === 'range';

const webKitPickerShellClassName =
  'border-input ring-offset-background flex h-9 w-full min-w-0 items-center rounded-md border bg-background touch:h-11';

const webKitPickerInputClassName =
  'min-w-0 flex-1 bg-transparent px-3 py-1 text-left text-sm touch:text-base file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50';

const Input = ({
  className,
  shellClassName,
  type,
  ignoreScrolling = false,
  ...props
}: InputProps) => {
  const isScrolling = useScroll();
  const disabled =
    props.disabled || (!ignoreScrolling && isScrolling && isScrollMutableInputType(type));

  if (isWebKitPickerInputType(type)) {
    return (
      <div className="relative">
        <div className={cn(webKitPickerShellClassName, shellClassName)}>
          <input
            type={type}
            className={cn(webKitPickerInputClassName, className)}
            disabled={disabled}
            {...props}
          />
        </div>
      </div>
    );
  }

  return (
    <input
      type={type}
      className={cn(
        'border-input placeholder:text-muted-foreground focus-visible:ring-ring touch:h-11 touch:text-base flex h-9 w-full rounded-md border bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:outline-none focus-visible:ring-1 disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      disabled={disabled}
      {...props}
    />
  );
};

const MoneyInput = ({
  className,
  type = 'number',
  ignoreScrolling = false,
  ...props
}: InputProps) => {
  const isScrolling = useScroll();
  return (
    <div className="relative">
      <div className="text-foreground absolute left-2 top-1/2 -translate-y-1/2 font-medium">$</div>
      <input
        disabled={
          props.disabled || (!ignoreScrolling && isScrolling && isScrollMutableInputType(type))
        }
        type={type}
        className={cn(
          'border-input placeholder:text-muted-foreground focus-visible:ring-ring flex h-9 w-full rounded-md border bg-transparent px-3 py-1 pl-6 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:outline-none focus-visible:ring-1 disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
        step={0.01}
        {...props}
      />
    </div>
  );
};

export { Input, MoneyInput };
